import React, { useState, useEffect } from "react";
import axios from 'axios';
import api from '../api';

export default function CardPropertiesPanel({ card, selectedTextOption, onSelectTextOption, language, onDelete, onDetach, symbols = [], onUserTextChange, userCanvases = [], currentCanvasId = null }) {

  if (!card) return null;
  const texts = (card.texts || []).filter(t => t.language === language);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportLanguage, setReportLanguage] = useState(language);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportSuccess, setReportSuccess] = useState('');
  const [languages, setLanguages] = useState([]);

  // Fetch available languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await api.get('/api/users/languages');
        if (Array.isArray(res.data)) {
          setLanguages(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch languages:', err);
      }
    };
    fetchLanguages();
  }, []);

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportReason.trim()) {
      setReportError('Please enter a reason for the report/suggestion.');
      return;
    }
    if (!reportLanguage) {
      setReportError('Please select a language.');
      return;
    }

    setReportSubmitting(true);
    setReportError('');
    setReportSuccess('');

    try {
      await api.post(`/api/cards/${card.card_id}/report`, {
        reason: reportReason.trim(),
        language: reportLanguage
      });
      setReportSuccess('Report submitted successfully! Thank you for your feedback.');
      setReportReason('');
      // Close modal after a short delay
      setTimeout(() => {
        setShowReportModal(false);
        setReportSuccess('');
      }, 2000);
    } catch (err) {
      setReportError(err?.response?.data?.error || err.message || 'Failed to submit report. Please try again.');
    }
    setReportSubmitting(false);
  };

  // Helper: parse text into parts (text segments and symbol references in order)
    function parseTextAndSymbols(text) {
      if (!text) return { parts: [] };
      const cleanText = text
        .replace(/^\{\{(red|blue|grey|purple|green|yellow)\}\}\s*/i, '')
        .replace(/^\{\{background:\d+\}\}\s*/i, '');
    
      // Split by {{symbol:N}} while keeping the delimiters (allow flexible whitespace, case-insensitive)
                  const parts = [];
                  const regex = /\{\{\s*symbol\s*:\s*(\d+)\s*\}\}/gi;
                  let lastIndex = 0;
                  let match;
    
                  while ((match = regex.exec(cleanText)) !== null) {
                    // Text before this symbol
                    if (match.index > lastIndex) {
                      const textSegment = cleanText.substring(lastIndex, match.index);
                      if (textSegment) {
                        parts.push({ type: 'text', content: textSegment });
                      }
                    }
                    // The symbol reference
                    const symbolId = parseInt(match[1], 10);
                    parts.push({ type: 'symbol', id: symbolId, raw: match[0] });
                    lastIndex = regex.lastIndex;
                  }
    
      // Remaining text after last symbol
      if (lastIndex < cleanText.length) {
        const textSegment = cleanText.substring(lastIndex);
        if (textSegment) {
          parts.push({ type: 'text', content: textSegment });
        }
      }
    
      return { parts };
    }

    // Helper: render text with symbol references inline within the text flow
    function renderTextWithSymbols(text, sizePx = 24) {
      if (!text) return null;
      const { parts } = parseTextAndSymbols(text);
    
      // If only a single symbol reference (no text), render large centered
      if (parts.length === 1 && parts[0].type === 'symbol') {
        const symbolId = parts[0].id;
        const symbol = symbols.find(s => s.symbol_id === symbolId);
        if (symbol && symbol.symbol) {
          return (
            <img
              src={symbol.symbol}
              alt={`Symbol ${symbolId}`}
              style={{ width: 64, height: 64, objectFit: 'contain', display: 'block', margin: 'auto' }}
            />
          );
        } else {
          return <span>{text}</span>;
        }
      }

      // Render text and symbols inline within a single span
          const elements = parts.map((part, idx) => {
            if (part.type === 'symbol') {
              const symbol = symbols.find(s => s.symbol_id === part.id);
              if (symbol && symbol.symbol) {
                return (
                  <img
                    key={`symbol-${idx}`}
                    src={symbol.symbol}
                    alt={`Symbol ${part.id}`}
                    style={{ 
                      width: sizePx, 
                      height: sizePx, 
                      objectFit: 'contain', 
                      display: 'inline-block', 
                      verticalAlign: 'middle',
                      margin: '0 2px'
                    }}
                  />
                );
              }
              // Fallback: render raw text if symbol not found
              return <span key={`symbol-fallback-${idx}`}>{part.raw}</span>;
            } else {
              // Text segment
              return (
                <span key={`text-${idx}`}>{part.content}</span>
              );
            }
          });

      return (
        <span style={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          width: '100%',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: 1.2
        }}>
          {elements}
        </span>
      );
    }

  // Check if this is a canvas reference card
  const isCanvasReference = card.deck_id === 0 && card.type_id === 0 &&
    card.user_text && /^\\{Canvas:.*\\}$/.test(card.user_text.trim());

  // Extract canvas ID from the text if it exists
  const canvasIdMatch = card.user_text ? card.user_text.match(/^\\{Canvas:(\\d*)\\}$/) : null;
  const selectedCanvasId = canvasIdMatch ? canvasIdMatch[1] : '';

  return (
    <div id="tour-card-properties" style={{
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <h3>Card Options</h3>
        {/* Special case: deck 0/type 0 user text input */}
        {card.deck_id === 0 && card.type_id === 0 ? (
          <div style={{ marginBottom: 16 }}>
            {isCanvasReference ? (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <label style={{ minWidth: 120, marginRight: 16, fontWeight: 'bold', textAlign: 'right' }}>
                  Canvas Reference:
                </label>
                <select
                  value={selectedCanvasId}
                  onChange={e => {
                    const newCanvasId = e.target.value;
                    const newText = `{Canvas:${newCanvasId}}`;
                    onUserTextChange && onUserTextChange(card, newText);
                  }}
                  style={{
                    flex: 1,
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    fontSize: 14
                  }}
                >
                  <option value="">Select a canvas...</option>
                  {userCanvases
                    .filter(canvas => canvas.canvas_id !== currentCanvasId)
                    .map(canvas => (
                      <option key={canvas.canvas_id} value={canvas.canvas_id}>
                        {canvas.name || `Canvas ${canvas.canvas_id}`}
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <div>
                <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Your Text:</label>
                <textarea
                  value={card.user_text || ''}
                  onChange={e => onUserTextChange && onUserTextChange(card, e.target.value)}
                  placeholder="Enter your text for this card..."
                  style={{ width: '100%', minHeight: 60, fontSize: 15, padding: 8, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>
            )}
          </div>
        ) : (
          texts.length === 0 ? (
            <div style={{ color: '#888' }}>No text options in this language.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {texts.map(t => {
                const isSelected = t.option_index === selectedTextOption;
                const isLocked = card.type_id === 7;
                return (
                  <div
                    key={t.option_index}
                    onClick={() => {
                      if (!isLocked) {
                        onSelectTextOption(t.option_index);
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      background: isSelected ? '#4a90e2' : '#f0f0f0',
                      color: isSelected ? '#fff' : '#222',
                      cursor: isLocked ? 'default' : 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      border: isSelected ? '2px solid #357abd' : '1px solid #ddd',
                      transition: 'all 0.15s',
                      opacity: isLocked && !isSelected ? 0.6 : 1,
                    }}
                  >
                    {renderTextWithSymbols(t.text, 18)}
                  </div>
                );
              })}
            </div>
          )
        )}
        <div style={{
          marginTop: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          paddingTop: 16,
          borderTop: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <button
            onClick={() => setShowReportModal(true)}
            style={{
              width: '100%',
              padding: '10px 0',
              background: '#f39c12',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 2px 8px #0002',
              transition: 'background 0.2s',
            }}
          >
            Report / Suggestion
          </button>
          <button
            onClick={onDelete}
            style={{
              width: '100%',
              padding: '10px 0',
              background: '#e74c3c',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 2px 8px #0002',
              transition: 'background 0.2s',
            }}
          >
            Delete Card
          </button>
        </div>
      </div>

      {/* Report/Suggestion Modal - INSIDE the outer div */}
      {showReportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: 20,
            boxSizing: 'border-box',
          }}
          onClick={() => setShowReportModal(false)}
        >
          <div
            style={{
              background: 'var(--surface, #fff)',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 500,
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowReportModal(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer',
                color: '#888',
                lineHeight: 1,
                padding: 4,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
              }}
              onMouseOver={(e) => e.target.style.background = '#eee'}
              onMouseOut={(e) => e.target.style.background = 'transparent'}
              aria-label="Close"
            >
              ×
            </button>
            <h3 style={{ marginTop: 0, marginBottom: 20, color: 'var(--text, #222)' }}>
              Report Card or Suggest Improvement
            </h3>
            <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
              Found an error, offensive content, or have a suggestion? Let us know.
              {reportReason && <span style={{ color: '#f39c12', fontWeight: 500 }}> If reporting a spelling error, please include the correct spelling.</span>}
            </p>
            <form onSubmit={handleReportSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: 'var(--text, #222)' }}>
                  Reason for Report / Suggestion *
                </label>
                <textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Describe the issue or suggestion... If there's a spelling error, please include the correct spelling."
                  style={{
                    width: '100%',
                    minHeight: 100,
                    padding: 12,
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--surface, #fff)',
                    color: 'var(--text, #222)',
                  }}
                  required
                />
                {reportError && (
                  <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 6 }}>{reportError}</div>
                )}
                {reportSuccess && (
                  <div style={{ color: '#27ae60', fontSize: 13, marginTop: 6 }}>{reportSuccess}</div>
                )}
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: 'var(--text, #222)' }}>
                  Language *
                </label>
                <select
                  value={reportLanguage}
                  onChange={(e) => setReportLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    background: 'var(--surface, #fff)',
                    color: 'var(--text, #222)',
                    boxSizing: 'border-box',
                  }}
                  required
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name} ({lang.code})
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={reportSubmitting}
                style={{
                  width: '100%',
                  padding: '12px 0',
                  background: reportSubmitting ? '#95a5a6' : '#4a90e2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: reportSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {reportSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}